import { Milk, Hand, Leaf, Ban } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";

const BADGES = [
  { icon: Milk, label: "A2 Gir Cow Milk" },
  { icon: Hand, label: "Bilona Churned" },
  { icon: Leaf, label: "No Preservatives" },
  { icon: Ban, label: "No Additives" },
];

const STEPS = [
  {
    title: "Fresh A2 Gir Cow Milk",
    text: "We start with fresh milk from Gir cows that are naturally raised and well cared for.",
  },
  {
    title: "Curd Setting",
    text: "The milk is boiled, cooled to the right temperature and cultured naturally to set curd.",
  },
  {
    title: "Bilona Churning",
    text: "The curd is hand-churned slowly with a traditional wooden bilona, separating the butter (makhan) from the buttermilk.",
  },
  {
    title: "Fresh Makhan",
    text: "The soft white butter collected is pure and fragrant — the heart of our ghee.",
  },
  {
    title: "Slow Heating",
    text: "The butter is gently heated on a low flame, melting slowly and releasing a rich aroma as it turns into pure ghee.",
  },
  {
    title: "Golden Bilona Ghee",
    text: "The ghee is filtered naturally and packed in hygienic conditions — pure, golden and aromatic, just as tradition intended.",
  },
];

export default function BilonaMethod() {
  return (
    <section id="bilona" data-testid="bilona-section" className="bg-ivory py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <SectionHeading
          eyebrow="The Craft"
          title={
            <>
              The Bilona Method.{" "}
              <span className="italic text-forest-light">A Timeless Way to Pure Ghee.</span>
            </>
          }
        >
          Bilona is an ancient Indian method of making ghee: curd is hand-churned
          to extract butter, which is then slow-heated into pure, aromatic
          ghee. No shortcuts, nothing added.
        </SectionHeading>

        <Reveal delay={0.2}>
          <div className="mt-10 flex flex-wrap gap-3">
            {BADGES.map((b) => (
              <span
                key={b.label}
                data-testid={`bilona-badge-${b.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-2.5 rounded-full border border-forest/15 bg-white/60 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-forest"
              >
                <b.icon className="h-4 w-4 text-gold" />
                {b.label}
              </span>
            ))}
          </div>
        </Reveal>

        <div className="mt-16 grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <Reveal>
                <div className="group relative">
                  <div
                    aria-hidden
                    className="absolute -inset-3 rounded-[32px] border border-gold/50"
                  />
                  <div className="overflow-hidden rounded-[28px] shadow-[0_40px_90px_-35px_rgba(26,54,38,0.45)]">
                    <img
                      data-testid="bilona-label-image"
                      src="/assets/label.jpg"
                      alt="MAHIRA A2 Gir Cow Ghee — official label, Traditional Bilona Method"
                      loading="lazy"
                      className="w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                    />
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-6 text-center text-[11px] font-semibold uppercase tracking-[0.3em] text-charcoal/45">
                  The official MAHIRA label
                </p>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-6">
            <div className="grid gap-5 sm:grid-cols-2">
              {STEPS.map((s, i) => (
                <Reveal key={s.title} delay={(i % 2) * 0.08}>
                  <div
                    data-testid={`bilona-step-${i + 1}`}
                    className="group h-full rounded-3xl border border-forest/10 bg-white/60 p-7 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_30px_60px_-25px_rgba(26,54,38,0.25)]"
                  >
                    <span className="font-heading text-4xl text-gold/60 transition-colors duration-500 group-hover:text-gold">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="mt-4 font-heading text-2xl text-forest">{s.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-charcoal/65">{s.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
